import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lightbulb, TrendingUp, Cloud, Users, Timer, Route, CalendarDays, Info, Zap, Map, MessageSquare } from 'lucide-react'
import { SkeletonCard } from '@/components/ui/Skeleton'
import { useAuth } from '@/contexts/AuthContext'
import { useEventData, type InsightCard as InsightCardType } from '@/contexts/EventDataContext'
import { useChat } from '@/contexts/ChatContext'
import { TransitGraph } from '@/components/TransitGraph'
import { DistanceHeatmap } from '@/components/DistanceHeatmap'

function CuratedBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-700">
      <Info className="h-2.5 w-2.5" />
      Curated
    </span>
  )
}

function LiveBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-200 px-2 py-0.5 text-[10px] font-medium text-green-700">
      <Zap className="h-2.5 w-2.5" />
      Live data
    </span>
  )
}

const WEATHER_CARDS = [
  { emoji: '☀️', condition: 'Clear / Sunny', impact: '+18%', desc: 'Revenue increases across all categories. Rides peak, food steady.', positive: true },
  { emoji: '⛅', condition: 'Partly Cloudy', impact: '+5%', desc: 'Slight boost — comfortable conditions drive longer stays.', positive: true },
  { emoji: '🌧️', condition: 'Light Rain', impact: '-22%', desc: 'Significant drop in ride revenue. Games and covered food hold.', positive: false },
  { emoji: '🌡️', condition: '95°F+', impact: '-15%', desc: 'Heat drives guests to shade. Water rides up +40%, games down -20%.', positive: false },
]

function formatDuration(secs: number): string {
  if (secs < 60) return `${Math.round(secs)}s`
  const mins = Math.floor(secs / 60)
  const s = Math.round(secs % 60)
  if (mins >= 60) {
    const hrs = Math.floor(mins / 60)
    const m = mins % 60
    return `${hrs}h ${m}m`
  }
  return s > 0 ? `${mins}m ${s}s` : `${mins}m`
}

export function InsightsPage() {
  const { currentEventId } = useAuth()
  const { intelligence, extras, entities, loading } = useEventData()

  const dayOfWeekBars = useMemo(() => {
    if (!extras?.daily_breakdown?.length) return []
    const byDay: Record<string, { revenue: number; count: number }> = {}
    for (const row of extras.daily_breakdown) {
      const d = new Date(row.day)
      const dayName = d.toLocaleDateString('en-US', { weekday: 'long' })
      if (!byDay[dayName]) byDay[dayName] = { revenue: 0, count: 0 }
      byDay[dayName].revenue += (row.revenue ?? 0)
      byDay[dayName].count += 1
    }
    const entries = Object.entries(byDay)
      .map(([day, { revenue, count }]) => ({ day, revenue, avgRevenue: Math.round(revenue / count), count }))
      .sort((a, b) => b.revenue - a.revenue)
    const maxRev = Math.max(...entries.map(e => e.revenue), 1)
    return entries.map(e => ({ ...e, pct: Math.round((e.revenue / maxRev) * 100) }))
  }, [extras])

  const waitTimeTable = useMemo(() => {
    if (!intelligence?.attraction_deep) return []
    return intelligence.attraction_deep
      .filter(a => a.type === 'Rides' && a.est_wait_avg_sec != null)
      .sort((a, b) => (b.est_wait_avg_sec ?? 0) - (a.est_wait_avg_sec ?? 0))
      .slice(0, 10)
  }, [intelligence])

  const guestEngagement = useMemo(() => {
    if (!intelligence?.insights) return null
    const card = intelligence.insights.find(c => c.title.toLowerCase().includes('guest'))
    return card ?? null
  }, [intelligence])

  const proximityInsights = useMemo(() => {
    if (!intelligence?.transit_graph?.proximity_revenue) return []
    return intelligence.transit_graph.proximity_revenue.slice(0, 8)
  }, [intelligence])

  const topTransitEdges = useMemo(() => {
    if (!intelligence?.transit_graph?.edges) return []
    return [...intelligence.transit_graph.edges]
      .sort((a, b) => b.observations - a.observations)
      .slice(0, 5)
  }, [intelligence])

  const topAttractions = useMemo(() => {
    if (!extras?.top_attractions) return []
    const maxRev = Math.max(...extras.top_attractions.map(a => a.revenue), 1)
    return extras.top_attractions.slice(0, 7).map(a => ({
      name: a.attraction_name,
      revenue: a.revenue,
      pct: Math.round((a.revenue / maxRev) * 100),
      type: a.attraction_type,
    }))
  }, [extras])

  const fleetEmployees = useMemo(() => {
    if (!entities?.employees?.humans) return []
    return [...entities.employees.humans]
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10)
      .map((e, i) => ({ rank: i + 1, ...e }))
  }, [entities])

  if (currentEventId == null) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
            <Lightbulb className="h-5 w-5 text-butler-600" />
          </div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Operational Insights</h1>
        </div>
        <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-ink-soft">
          Select an event to see operational insights.
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-butler-50">
          <Lightbulb className="h-5 w-5 text-butler-600" />
        </div>
        <div>
          <h1 className="font-display font-bold text-xl text-ink-dark">Operational Insights</h1>
          <p className="text-sm text-ink-soft">Intelligence from real operational data</p>
        </div>
      </div>

      {loading && !intelligence && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <SkeletonCard /><SkeletonCard /><SkeletonCard />
        </div>
      )}

      {/* Guest Engagement highlight */}
      {guestEngagement && (
        <div className="mm-card-v3 p-5 border-l-4 border-butler-500">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{guestEngagement.icon}</span>
            <h2 className="font-display font-semibold text-base text-ink-dark">{guestEngagement.title}</h2>
            <LiveBadge />
          </div>
          <p className="text-sm text-ink-soft leading-relaxed">{guestEngagement.body}</p>
        </div>
      )}

      {/* Auto-generated insight cards from intelligence */}
      {intelligence?.insights && intelligence.insights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Key Findings</h2>
            <LiveBadge />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {intelligence.insights
              .filter(c => !c.title.toLowerCase().includes('guest'))
              .map((card, i) => (
                <InsightCardWithEvidence key={i} card={card} />
              ))}
          </div>
        </section>
      )}

      {/* Wait Times & Cycle Analysis — from attraction_deep */}
      {waitTimeTable.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Timer className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Wait Times & Ride Cycles</h2>
            <LiveBadge />
          </div>
          <div className="mm-card-v3 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Ride</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Wait</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Peak Wait</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Cycle Time</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Batch</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Throughput</th>
                </tr>
              </thead>
              <tbody>
                {waitTimeTable.map(a => (
                  <tr key={a.attraction_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-sm font-medium text-ink-dark">{a.name}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm">
                      {a.est_wait_avg_sec != null ? formatDuration(a.est_wait_avg_sec) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-red-600">
                      {a.est_wait_worst_sec != null ? formatDuration(a.est_wait_worst_sec) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">
                      {a.cycle_time_sec != null ? formatDuration(a.cycle_time_sec) : '—'}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">
                      {a.batch_size ?? '—'}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">
                      {a.throughput_per_min != null ? `${a.throughput_per_min.toFixed(1)}/min` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Transit Distances — from transit_graph */}
      {topTransitEdges.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Route className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Transit Between Attractions</h2>
            <LiveBadge />
          </div>
          <div className="mm-card-v3 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Route</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Distance</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Median Time</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Walk Est.</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Observations</th>
                </tr>
              </thead>
              <tbody>
                {topTransitEdges.map((e, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-sm text-ink-dark">
                      <span className="font-medium">{e.from_name}</span>
                      <span className="text-ink-soft mx-1">→</span>
                      <span className="font-medium">{e.to_name}</span>
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm">{Math.round(e.distance_m)}m</td>
                    <td className="py-3 px-4 text-right mm-data text-sm">{formatDuration(e.median_sec)}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">{formatDuration(e.walk_sec)}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">{e.observations.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Proximity-Revenue Correlation */}
      {proximityInsights.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Proximity vs Revenue</h2>
            <LiveBadge />
          </div>
          <p className="text-xs text-ink-soft mb-3">
            Does proximity to popular rides drive more revenue? Sorted by distance to top rides.
          </p>
          <div className="mm-card-v3 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Attraction</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Type</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Revenue</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Avg Dist to Top Rides</th>
                </tr>
              </thead>
              <tbody>
                {proximityInsights.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4 text-sm font-medium text-ink-dark">{r.name}</td>
                    <td className="py-3 px-4 text-xs text-ink-soft">{r.type}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-dark">
                      ${r.revenue.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-sm text-ink-soft">
                      {Math.round(r.avg_dist_to_top_rides_m)}m
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Transit Graph Visualization */}
      {intelligence?.transit_graph && intelligence.transit_graph.nodes.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Map className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Midway Layout (Derived from Scans)</h2>
            <LiveBadge />
          </div>
          <TransitGraph data={intelligence.transit_graph} />
        </section>
      )}

      {/* Distance Heatmap */}
      {intelligence?.transit_graph && intelligence.transit_graph.nodes.length >= 3 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Route className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Attraction Distance Matrix</h2>
            <LiveBadge />
          </div>
          <div className="mm-card-v3 p-5">
            <DistanceHeatmap data={intelligence.transit_graph} />
          </div>
        </section>
      )}

      {/* Revenue Velocity */}
      {intelligence?.velocity && intelligence.velocity.points.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Revenue Velocity</h2>
            <LiveBadge />
          </div>
          <div className="mm-card-v3 p-5">
            <div className="flex items-end gap-[3px] h-24">
              {intelligence.velocity.points.map(p => {
                const maxRev = intelligence.velocity.peak_revenue || 1
                const pct = Math.round((p.revenue / maxRev) * 100)
                return (
                  <div
                    key={p.hour}
                    className="flex-1 rounded-t-sm"
                    style={{
                      height: `${Math.max(pct, 2)}%`,
                      background: p.hour === intelligence.velocity.peak_hour
                        ? '#0070ab'
                        : 'rgba(0, 112, 171, 0.4)',
                    }}
                    title={`${p.hour}:00 — $${p.revenue.toLocaleString()} (${p.orders} orders)`}
                  />
                )
              })}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-ink-soft">
              <span>{intelligence.velocity.points[0]?.hour}:00</span>
              <span className="font-medium text-butler-600">
                Peak: {intelligence.velocity.peak_hour}:00 (${intelligence.velocity.peak_revenue.toLocaleString()})
              </span>
              <span>{intelligence.velocity.points[intelligence.velocity.points.length - 1]?.hour}:00</span>
            </div>
          </div>
        </section>
      )}

      {/* Day-of-Week Revenue */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="h-4 w-4 text-butler-600" />
          <h2 className="font-display font-semibold text-base text-ink-dark">Revenue by Day of Week</h2>
          <LiveBadge />
        </div>
        {dayOfWeekBars.length === 0 ? (
          <div className="mm-card-v3 p-6 text-center text-sm text-ink-soft">
            No daily breakdown data available.
          </div>
        ) : (
          <div className="mm-card-v3 p-5 space-y-3">
            {dayOfWeekBars.map(d => (
              <div key={d.day} className="flex items-center gap-3">
                <span className="w-24 text-xs font-medium text-ink-dark shrink-0">{d.day}</span>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${d.pct}%`, background: 'linear-gradient(90deg, #0070ab, #00c7ca)' }}
                  />
                </div>
                <span className="mm-data text-xs font-medium text-ink-dark w-20 text-right">
                  ${d.revenue >= 1000 ? `${(d.revenue / 1000).toFixed(1)}K` : d.revenue.toLocaleString()}
                </span>
                {d.count > 1 && (
                  <span className="text-[10px] text-ink-soft w-16 text-right">{d.count} days</span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Top Attractions + Weather side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="mm-card-v3 p-5">
          <h3 className="font-display font-semibold text-sm text-ink-dark mb-4">Revenue by Top Attractions</h3>
          {topAttractions.length === 0 ? (
            <div className="py-6 text-center text-xs text-ink-soft">No attraction data.</div>
          ) : (
            <div className="space-y-2.5">
              {topAttractions.map(a => (
                <div key={a.name} className="flex items-center gap-3">
                  <span className="w-28 text-xs text-ink-soft shrink-0 truncate" title={a.name}>{a.name}</span>
                  <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${a.pct}%`, background: 'linear-gradient(90deg, #0070ab, #00c7ca)' }}
                    />
                  </div>
                  <span className="mm-data text-xs font-medium text-ink-dark w-16 text-right">
                    ${a.revenue >= 1000 ? `${(a.revenue / 1000).toFixed(1)}K` : a.revenue.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section>
          <div className="flex items-center gap-2 mb-3">
            <Cloud className="h-4 w-4 text-butler-600" />
            <h3 className="font-display font-semibold text-sm text-ink-dark">Weather Impact</h3>
            <CuratedBadge />
          </div>
          <div className="space-y-2">
            {WEATHER_CARDS.map((w, i) => (
              <div key={i} className="mm-card-v3 p-3 flex items-start gap-3">
                <span className="text-2xl shrink-0">{w.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-ink-dark">{w.condition}</span>
                    <span className={`mm-data text-xs font-bold ${w.positive ? 'text-green-700' : 'text-red-600'}`}>
                      {w.impact}
                    </span>
                  </div>
                  <p className="text-xs text-ink-soft mt-0.5">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Fleet Employee Rankings */}
      {fleetEmployees.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-base text-ink-dark">Fleet Intelligence</h2>
            <LiveBadge />
          </div>
          <div className="mm-card-v3 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft w-12">#</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Employee</th>
                  <th className="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-ink-soft">Attraction</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Revenue</th>
                  <th className="py-3 px-4 text-right text-xs font-semibold uppercase tracking-wide text-ink-soft">Scans/hr</th>
                </tr>
              </thead>
              <tbody>
                {fleetEmployees.map(emp => (
                  <tr key={`${emp.rank}-${emp.name}`} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 px-4">
                      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                        emp.rank <= 3 ? 'bg-butler-50 text-butler-700' : 'bg-slate-50 text-ink-soft'
                      }`}>
                        {emp.rank}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-sm font-medium text-ink-dark">{emp.name}</div>
                      <div className="text-xs text-ink-soft">{emp.role}</div>
                    </td>
                    <td className="py-3 px-4 text-sm text-ink-soft">{emp.assigned_attraction}</td>
                    <td className="py-3 px-4 text-right mm-data text-sm font-medium text-ink-dark">
                      ${emp.revenue.toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right mm-data text-xs text-ink-soft">
                      {emp.scans_per_hour.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  )
}

function InsightCardWithEvidence({ card }: { card: InsightCardType }) {
  const [expanded, setExpanded] = useState(false)
  const hasEvidence = card.evidence && card.evidence.length > 0
  const { setPendingMessage } = useChat()
  const navigate = useNavigate()

  const askAbout = () => {
    setPendingMessage(`Tell me more about: ${card.title}. ${card.body}`)
    navigate('/home')
  }

  return (
    <div className="mm-card-v3 p-4 relative group">
      <div className="absolute top-3 right-3 flex items-center gap-1">
        <button
          type="button"
          onClick={askAbout}
          className="rounded-full p-1 text-ink-soft/0 group-hover:text-butler-500 hover:!bg-butler-50 transition-colors"
          title="Ask the agent about this"
        >
          <MessageSquare className="h-3.5 w-3.5" />
        </button>
        {hasEvidence && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className={`rounded-full p-1 transition-colors ${
              expanded
                ? 'bg-butler-100 text-butler-700'
                : 'text-ink-soft/40 hover:text-butler-600 hover:bg-butler-50'
            }`}
            title="How was this determined?"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="flex items-start gap-3 pr-14">
        <span className="text-xl shrink-0">{card.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-ink-dark">{card.title}</span>
            {card.type === 'warning' && (
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400" />
            )}
          </div>
          <p className="text-xs text-ink-soft mt-1 leading-relaxed">{card.body}</p>
        </div>
      </div>
      {expanded && hasEvidence && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
          {card.method && (
            <p className="text-[10px] text-ink-soft italic leading-relaxed">
              <span className="font-semibold not-italic">Method:</span> {card.method}
            </p>
          )}
          <div className="space-y-1">
            {card.evidence!.map((ev, i) => (
              <div key={i} className="flex items-center justify-between text-[10px]">
                <span className="text-ink-soft">
                  <span className="font-mono text-butler-600">{ev.source}</span>
                  <span className="mx-1">·</span>
                  {ev.label}
                </span>
                <span className="font-mono font-medium text-ink-dark">
                  {typeof ev.value === 'number' ? ev.value.toLocaleString() : ev.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
